import { Box, Heading, Stack, StackProps, Text } from "@biblioteksentralen/react";
import { ResolvedRecommendation } from "@libry-content/common";
import { catalogueLanguageCode, getCanonicalLanguageCode } from "@libry-content/localization";
import { Publication } from "@libry-content/types";
import { ReactNode } from "react";
import { languageCodesEquivalent } from "../../utils/hooks/usePublicationLangAttribute";
import { useTranslation } from "../../utils/hooks/useTranslation";
import { DefaultContainer } from "../ContentContainer";
import CreatedByTag from "../employees/CreatedByTag";
import AppealTerms from "../forrigeBok/AppealTerms";
import ReadAlikes from "../forrigeBok/ReadAlikes";
import { useForrigeBokWork } from "../forrigeBok/useForrigeBokData";
import PublicationLink from "./PublicationLink";
import { RecommendationDescription } from "./RecommendationDescription";
import { RecommendationHero } from "./RecommendationHero";
import SubjectTags from "./SubjectTags";
import { Teaser } from "./Teaser";
import { useRecommendationPalette } from "./utils";

type Props = {
  recommendation: ResolvedRecommendation;
};

// Summaries are usually written in bokmål (the catalogue language), except for nynorsk
// publications, which are usually written in nynorsk.
const getSummaryLanguageCode = (publication: ResolvedRecommendation["publication"]): string | undefined => {
  if (!publication?.language) return undefined;
  if (languageCodesEquivalent("no", publication?.language)) return getCanonicalLanguageCode(publication?.language);
  return catalogueLanguageCode;
};

const RecommendationView = ({ recommendation }: Props) => {
  const { createdByPalette } = useRecommendationPalette(recommendation);

  if (!recommendation?.publication) return null;

  const { createdBy } = recommendation;
  return (
    <>
      <DefaultContainer withoutPaddingXBelow="md">
        <RecommendationHero recommendation={recommendation} headingSize="h1">
          <Stack>
            <SubjectTags publication={recommendation.publication} />
            <PublicationLink publication={recommendation.publication} />
          </Stack>
        </RecommendationHero>
      </DefaultContainer>
      <DefaultContainer>
        <Stack gap="3rem" margin="2rem 0 4rem">
          <Section maxWidth="35rem">
            <Stack gap="1rem">
              <Teaser recommendation={recommendation} />
              <RecommendationDescription recommendation={recommendation} />
              <CreatedByTag createdBy={createdBy} size="md" paletteColor={createdByPalette} />
            </Stack>
          </Section>
          <AboutMediaSection publication={recommendation.publication} />
          {recommendation.publication.isbn && <ForrigebokData isbn={recommendation.publication.isbn} />}
        </Stack>
      </DefaultContainer>
    </>
  );
};

const AboutMediaSection = ({ publication }: { publication: Publication }) => {
  const { t, lang } = useTranslation();
  const summaryLanguageCode = getSummaryLanguageCode(publication);
  const summaryLangAttribute =
    summaryLanguageCode && !languageCodesEquivalent(summaryLanguageCode, lang) ? summaryLanguageCode : undefined;

  if (!publication.summary) return null;

  const workType = publication.workType;

  const getAboutWorkType = (workType: Publication["workType"]) => {
    if (workType === "Interactive work") return t("Om spillet");
    if (workType === "Moving image work") return t("Om filmen");
    if (workType === "Musical work") return t("Om musikken");
    return t("Om boken");
  };

  return (
    <Section title={getAboutWorkType(workType)} lang={summaryLangAttribute} maxWidth="35rem">
      <Text>{publication.summary}</Text>
    </Section>
  );
};

const ForrigebokData = (props: { isbn: string }) => {
  const { t } = useTranslation();
  const work = useForrigeBokWork({ isbn: props.isbn });

  if (!work) return null;

  return (
    <>
      <Section title={t("Leseopplevelse")}>
        <AppealTerms appealTerms={work.appealTerms} />
      </Section>

      <ReadAlikes workId={work.id} />
    </>
  );
};

const Section = ({
  title,
  children,
  lang,
  ...stackProps
}: { title?: string; children: ReactNode; lang?: string } & StackProps) => (
  <Stack lang={lang} {...stackProps}>
    {title && (
      <Heading as="h2" size="md">
        {title}
      </Heading>
    )}
    <Box>{children}</Box>
  </Stack>
);

export default RecommendationView;
